import type { Container } from '@packages/core/container';

import { StsBackendClient } from '@packages/clients';
import { ANALYTICS_EVENTS, AnalyticsService } from '@packages/core/analytics';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { type TechlineStatusCode } from '../../cases/types';
import { buildCommentBody } from '../helpers';

/**
 * Parameters for adding a comment to a techline case.
 * Combines the comment content with the case context needed to build the API payload.
 */
export type AddCommentParams = {
    /** The case number to add the comment to. */
    caseNumber: string;

    /** The current case status code, included in the comment payload for the backend. */
    caseStatusCode: TechlineStatusCode;

    /** The comment text to post. */
    message: string;

    /** The retailer number associated with the case. */
    retailerNumber: string;

    /** The username of the comment author. */
    username: string;
};

/**
 * Add a comment to a techline case. Validates that the message is non-empty,
 * logs an analytics event, and posts the comment via the API. The backend
 * handles status transitions internally (e.g., IN_REVIEW to PENDING TECHLINE).
 */
export class AddComment extends StsBackendOperation {
    /**
     * Register this operation with additional dependencies beyond the backend client.
     *
     * @param container - the container to register in
     */
    static override registerInContainer(container: Container): void {
        container.register(AddComment, [StsBackendClient, AnalyticsService]);
    }

    /** The analytics service for logging comment events. */
    private analyticsService: AnalyticsService;

    /**
     * @param client - the backend API client
     * @param analyticsService - the analytics service for logging events
     */
    constructor(client: StsBackendClient, analyticsService: AnalyticsService) {
        super(client);
        this.analyticsService = analyticsService;
    }

    /**
     * Post a comment to the case. The status string is embedded in the comment
     * payload via `buildCommentBody`; the backend handles any status transitions
     * internally. Returns true if the comment was successfully posted.
     *
     * @param params - the comment details and case context
     * @returns whether the comment was successfully posted
     */
    async exec(params: AddCommentParams): Promise<boolean> {
        const { caseNumber, caseStatusCode, message, retailerNumber, username } = params;

        // Bail out if the message is empty
        if (!message) {
            return false;
        }

        // Log the analytics event before posting
        this.analyticsService.logEvent(ANALYTICS_EVENTS.USER_ADDS_COMMENT);

        // Build the API payload and post the comment
        const body = buildCommentBody({ caseNumber, caseStatusCode, message, retailerNumber, username });
        const response = await this.client.addComment({ body });

        return Boolean(response?.success);
    }
}
